import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Check, X } from 'lucide-react';

interface FormData {
  username: string;
  password: string;
  email: string;
  name: string;
  surname: string;
  date_of_birth: string;
  address_street?: string;
  address_town?: string;
  address_zipcode?: string;
  address_region?: string;
  address_state?: string;
}

interface FieldErrors {
  username?: string;
  email?: string;
  password?: string;
  [key: string]: string | undefined;
}

interface PasswordRequirement {
  regex: RegExp;
  label: string;
}

const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  { regex: /.{8,}/, label: 'At least 8 characters long' },
  { regex: /[A-Z]/, label: 'Contains uppercase letter' },
  { regex: /[a-z]/, label: 'Contains lowercase letter' },
  { regex: /[0-9]/, label: 'Contains number' },
  { regex: /[^A-Za-z0-9]/, label: 'Contains special character' }
];

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    username: '',
    password: '',
    email: '',
    name: '',
    surname: '',
    date_of_birth: '',
    address_street: '',
    address_town: '',
    address_zipcode: '',
    address_region: '',
    address_state: ''
  });

  const validatePassword = (password: string) => {
    return PASSWORD_REQUIREMENTS.every(req => req.regex.test(password));
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateUsername = (username: string) => {
    const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/;
    return usernameRegex.test(username);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for the field being changed
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }

    // Validate fields on change
    if (name === 'email' && value && !validateEmail(value)) {
      setFieldErrors(prev => ({
        ...prev,
        email: 'Please enter a valid email address'
      }));
    }
    
    if (name === 'username' && value && !validateUsername(value)) {
      setFieldErrors(prev => ({
        ...prev,
        username: 'Username must be 3-20 characters and contain only letters, numbers, and underscores'
      }));
    }
  };

  const getInputClassName = (fieldName: string) => {
    const baseClass = "relative block w-full rounded-lg border-0 p-3 text-gray-900 ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:outline-none transition-colors duration-200";
    return `${baseClass} ${
      fieldErrors[fieldName] 
        ? "ring-red-500 focus:ring-red-500 border-red-500" 
        : "ring-gray-300 focus:ring-indigo-600 hover:ring-indigo-400"
    }`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setIsSubmitting(true);

    // Validate all fields before submission
    let hasErrors = false;
    const newFieldErrors: FieldErrors = {};

    if (!validateEmail(formData.email)) {
      newFieldErrors.email = 'Please enter a valid email address';
      hasErrors = true;
    }

    if (!validateUsername(formData.username)) {
      newFieldErrors.username = 'Username must be 3-20 characters and contain only letters, numbers, and underscores';
      hasErrors = true;
    }

    if (!validatePassword(formData.password)) {
      newFieldErrors.password = 'Password does not meet all requirements';
      hasErrors = true;
    }

    if (hasErrors) {
      setFieldErrors(newFieldErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      const { address_street, address_town, address_zipcode, address_region, address_state, ...requiredData } = formData;
      
      // Add additional security measures
      const sanitizedData = {
        ...requiredData,
        email: requiredData.email.toLowerCase().trim(),
        username: requiredData.username.trim()
      };

      const success = await register(sanitizedData);

      if (success) {
        navigate('/login', { 
          state: { 
            registrationSuccess: true,
            userEmail: sanitizedData.email 
          }
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Registration failed';
      handleRegistrationError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegistrationError = (errorMessage: string) => {
    if (errorMessage.toLowerCase().includes('username already exists')) {
      setFieldErrors(prev => ({
        ...prev,
        username: 'This username is already taken'
      }));
    } else if (errorMessage.toLowerCase().includes('email already exists')) {
      setFieldErrors(prev => ({
        ...prev,
        email: 'This email is already registered'
      }));
    } else if (errorMessage.includes('Missing required field')) {
      const fieldName = errorMessage.split(':')[1]?.trim();
      if (fieldName) {
        setFieldErrors(prev => ({
          ...prev,
          [fieldName]: `${fieldName} is required`
        }));
      } else {
        setError('Please fill in all required fields');
      }
    } else {
      setError(errorMessage);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-md bg-white rounded-xl shadow-lg p-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900">
            Create your account
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Join our community and get started today
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="space-y-5">
            <div className="space-y-4">
              <div className="space-y-1">
                <input
                  type="text"
                  name="username"
                  required
                  className={getInputClassName("username")}
                  placeholder="Username"
                  value={formData.username}
                  onChange={handleChange}
                  aria-invalid={!!fieldErrors.username}
                  aria-describedby={fieldErrors.username ? "username-error" : undefined}
                />
                {fieldErrors.username && (
                  <p className="text-sm text-red-600" id="username-error">
                    {fieldErrors.username}
                  </p>
                )}
              </div>

              <div className="space-y-1 relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  required
                  className={getInputClassName("password")}
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
                {fieldErrors.password && (
                  <p className="text-sm text-red-600">{fieldErrors.password}</p>
                )}
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Password requirements:</p>
                <div className="space-y-2">
                  {PASSWORD_REQUIREMENTS.map((req, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      {req.regex.test(formData.password) ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <X className="w-4 h-4 text-gray-300" />
                      )}
                      <span className="text-sm text-gray-600">{req.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-1">
                <input
                  type="email"
                  name="email"
                  required
                  className={getInputClassName("email")}
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                />
                {fieldErrors.email && (
                  <p className="text-sm text-red-600">{fieldErrors.email}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  name="name"
                  required
                  className={getInputClassName("name")}
                  placeholder="First Name"
                  value={formData.name}
                  onChange={handleChange}
                />

                <input
                  type="text"
                  name="surname"
                  required
                  className={getInputClassName("surname")}
                  placeholder="Last Name"
                  value={formData.surname}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label htmlFor="date_of_birth" className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth
                </label>
                <input
                  type="date"
                  id="date_of_birth"
                  name="date_of_birth"
                  required
                  className={getInputClassName("date_of_birth")}
                  value={formData.date_of_birth}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="group relative flex w-full justify-center rounded-lg bg-indigo-600 px-4 py-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-70"
            >
              {isSubmitting ? 'Creating account...' : 'Create account'}
            </button>
          </div>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <button
              onClick={() => navigate('/login')}
              className="font-semibold text-indigo-600 hover:text-indigo-500 transition-colors duration-200"
            >
              Sign in instead
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;